import type { ReactNode } from 'react';
import { Filter, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SearchableSelect } from '@/components/core/searchable-select/searchable-select';
import { Button } from '@/components/ui-kit/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import { DepartmentFilter } from '@/modules/settings/organization/departments';
import { useDepartmentScope } from '@/modules/shared/hr-lookups';

interface LeaveCalendarFiltersProps {
  departmentId: string;
  onDepartmentChange: (value: string) => void;
  categoryId: string;
  onCategoryChange: (value: string) => void;
  categories: { ItemId: string; Title: string }[];
  status: string;
  onStatusChange: (value: string) => void;
}

const STATUSES = ['Approved', 'Pending', 'Rejected'] as const;

interface ActiveFilter {
  key: string;
  label: string;
  text: string;
  clear: () => void;
}

/**
 * The calendar's filters (prototype `filterBar`): one Filters button with a count, a popover holding
 * the fields, and up to two removable chips; "+N" and "Clear all" when more are on.
 */
export const LeaveCalendarFilters = ({
  departmentId,
  onDepartmentChange,
  categoryId,
  onCategoryChange,
  categories,
  status,
  onStatusChange,
}: LeaveCalendarFiltersProps) => {
  const { t } = useTranslation();
  const { departments } = useDepartmentScope();

  const active: ActiveFilter[] = [];
  if (departmentId)
    active.push({
      key: 'department',
      label: t('DEPARTMENT'),
      text: departments.find((department) => department.ItemId === departmentId)?.Title ?? '',
      clear: () => onDepartmentChange(''),
    });
  if (categoryId)
    active.push({
      key: 'category',
      label: t('LEAVE_CATEGORY'),
      text: categories.find((category) => category.ItemId === categoryId)?.Title ?? '',
      clear: () => onCategoryChange(''),
    });
  if (status)
    active.push({
      key: 'status',
      label: t('STATUS'),
      text: t(status.toUpperCase()),
      clear: () => onStatusChange(''),
    });

  const clearAll = () => active.forEach((filter) => filter.clear());

  const field = (label: string, isOn: boolean, clear: () => void, control: ReactNode) => (
    <div className="grid gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        {isOn && (
          <Button type="button" variant="link" size="sm" onClick={clear}>
            {t('CLEAR')}
          </Button>
        )}
      </div>
      {control}
    </div>
  );

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" aria-pressed={active.length > 0}>
            <Filter className="size-4" />
            {t('LEAVE_CALENDAR_FILTERS')}
            {active.length > 0 && (
              <span className="grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-xs font-medium tabular-nums text-primary-foreground">
                {active.length}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="grid w-[340px] gap-4 p-4">
          {field(
            t('DEPARTMENT'),
            Boolean(departmentId),
            () => onDepartmentChange(''),
            <DepartmentFilter
              value={departmentId}
              onChange={onDepartmentChange}
              selectClassName="w-full"
            />
          )}
          {field(
            t('LEAVE_CATEGORY'),
            Boolean(categoryId),
            () => onCategoryChange(''),
            <SearchableSelect
              value={categoryId}
              options={[
                { value: '', label: t('LEAVE_CALENDAR_ALL_CATEGORIES') },
                ...categories.map((category) => ({
                  value: category.ItemId,
                  label: category.Title,
                })),
              ]}
              onChange={onCategoryChange}
              placeholder={t('LEAVE_CATEGORY')}
              searchPlaceholder={t('SEARCH')}
              emptyText={t('NO_RESULTS_FOUND')}
              className="w-full"
            />
          )}
          {field(
            t('STATUS'),
            Boolean(status),
            () => onStatusChange(''),
            <SearchableSelect
              value={status}
              options={[
                { value: '', label: t('LEAVE_CALENDAR_ALL_STATUSES') },
                ...STATUSES.map((value) => ({ value, label: t(value.toUpperCase()) })),
              ]}
              onChange={onStatusChange}
              placeholder={t('STATUS')}
              searchPlaceholder={t('SEARCH')}
              emptyText={t('NO_RESULTS_FOUND')}
              className="w-full"
            />
          )}
        </PopoverContent>
      </Popover>

      {active.slice(0, 2).map((filter) => (
        <span
          key={filter.key}
          className="inline-flex h-8 max-w-[260px] items-center gap-1 rounded-full border border-tint-blue-border bg-accent pl-3 pr-1 text-[13px] text-accent-foreground"
        >
          <span className="truncate">
            {filter.label}: <b className="font-semibold">{filter.text}</b>
          </span>
          <button
            type="button"
            onClick={filter.clear}
            aria-label={t('LEAVE_CALENDAR_REMOVE_FILTER', { filter: filter.label })}
            className="grid size-6 shrink-0 place-items-center rounded-full outline-none hover:bg-background focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="size-3.5" />
          </button>
        </span>
      ))}
      {active.length > 2 && (
        <span className="text-[13px] text-muted-foreground">
          {t('LEAVE_CALENDAR_MORE_FILTERS', { count: active.length - 2 })}
        </span>
      )}
      {active.length > 0 && (
        <Button type="button" variant="link" onClick={clearAll}>
          {t('LEAVE_CALENDAR_CLEAR_ALL')}
        </Button>
      )}
    </>
  );
};
